import React from 'react';
import { BarChart, Bar, XAxis, YAxis } from 'recharts';
import { motion } from "framer-motion";
import { cn } from "@/lib/utils";
import {
  ChartConfig,
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";

export interface InteractiveBarChartProps {
  data: Array<Record<string, string | number>>;
  config: ChartConfig;
  dataKeys: string[];
  className?: string;
  emptyStateMessage?: string;
  emptyStateSubMessage?: string;
  isLoading?: boolean;
  dateFormatter?: (value: string) => string;
  tooltipLabelFormatter?: (value: string) => string;
  tooltipFormatter?: (value: any, name: string, entry: any) => [string, string] | null;
  customTooltip?: React.ReactElement;
  customLegend?: React.ReactElement;
  stacked?: boolean;
  hideLabel?: boolean;
}

const defaultDateFormatter = (value: string) => {
  const date = new Date(value);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
};

const defaultTooltipLabelFormatter = (value: string) => {
  return new Date(value).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

export const InteractiveBarChart: React.FC<InteractiveBarChartProps> = ({
  data,
  config,
  dataKeys,
  className,
  emptyStateMessage = "No data available",
  emptyStateSubMessage = "Data will appear here once available",
  isLoading = false,
  dateFormatter = defaultDateFormatter,
  tooltipLabelFormatter = defaultTooltipLabelFormatter,
  tooltipFormatter,
  customTooltip,
  customLegend,
  stacked = true,
  hideLabel = false,
}) => {
  const hasData = data && data.length > 0;

  const renderChart = () => (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.15, ease: "easeOut" }}
      className="h-full"
      style={{ willChange: "opacity" }}
    >
      {!hasData ? (
        <div className="flex items-center justify-center h-full flex-col">
          <p className="text-muted-foreground">{emptyStateMessage}</p>
          <p className="text-xs mt-2 text-muted-foreground">{emptyStateSubMessage}</p>
        </div>
      ) : (
        <ChartContainer config={config} className="aspect-auto w-full h-[320px]">
          <BarChart accessibilityLayer data={data}>
            <XAxis
              dataKey="date"
              tickLine={false}
              tickMargin={10}
              axisLine={true}
              tickFormatter={dateFormatter}
            />
            <YAxis
              tickLine={false}
              axisLine={true}
              tickMargin={8}
              tickFormatter={(value) => `${value}`}
            />
            <ChartTooltip 
              content={customTooltip ? customTooltip : (
                <ChartTooltipContent 
                  hideLabel={hideLabel}
                  labelFormatter={tooltipLabelFormatter}
                  formatter={tooltipFormatter}
                />
              )} 
            />
            <ChartLegend content={customLegend ? customLegend : <ChartLegendContent />} />
            {dataKeys.map((key, index) => {
              // Get color from chart config, fallback to CSS custom property
              const colorFromConfig = config[key]?.color;
              const fillColor = colorFromConfig || `var(--color-${key})`;
              
              return (
                <Bar
                  key={key}
                  dataKey={key}
                  stackId={stacked ? "a" : undefined}
                  fill={fillColor}
                  radius={
                    stacked 
                      ? index === 0 
                        ? [0, 0, 4, 4] 
                        : index === dataKeys.length - 1 
                          ? [4, 4, 0, 0] 
                          : [0, 0, 0, 0]
                      : [4, 4, 0, 0]
                  }
                />
              );
            })}
          </BarChart>
        </ChartContainer>
      )}
    </motion.div>
  );

  if (isLoading) {
    return (
      <div className={cn("flex items-center justify-center h-full", className)}>
        <div className="animate-pulse h-64 w-full bg-muted rounded-lg" />
      </div>
    );
  }

  return (
    <div className={cn("h-[320px]", className)}>
      {renderChart()}
    </div>
  );
};

export default InteractiveBarChart; 